import * as Yup from "yup";
import {
  distinctEmail,
  emailConfirm,
  validateDateHandler,
  validateEmail,
  validateFile,
  validateIfHaveAtLeastLetter,
  validateIfHaveAtLeastLetterRequired,
  validateLegalRut,
  validateName,
  validateNativeRut,
  validateNumber,
  validateYear,
} from "presentation/common";

import AppSettings from "application/globals/app-settings";

const addDistinctValidation = (prevValidation, context, field, message) => {
  return prevValidation.test("distinct-element", message, (value) => {
    if (context.parent.isOwner === "1") return true;

    const input =
      context.parent.personType === "NATURAL"
        ? context.parent?.personInfo?.[field]
        : context.parent?.ownerBillInfo?.[field];

    return value !== input;
  });
};

const calculateMapperTypes = (files) =>
  files?.reduce((acc, doc) => {
    acc[doc.type] = acc[doc.type] ? acc[doc.type] + 1 : 1;
    return acc;
  }, {}) ?? {};

Yup.addMethod(Yup.array, "uniqueDocs", function () {
  return this.test(
    "uniqueDocs",
    "No se debe repetir el tipo documental.",
    function (list) {
      const types = calculateMapperTypes(list);
      return !(types?.["DS55"] > 1 || types?.["Factura"] > 1);
    }
  );
});

// 3- Si se presiona el botón “Siguiente” se debe tener en cuenta la siguiente validación:
// No se adjunta factura: “La factura es requerida.”

// Para tipo de vehículo "CHASIS CABINADO" o "CAMION"
// No se adjunta DS55: “El DS55 es requerido”.
// No se adjunta ni factura ni DS55: “La factura y el DS55 son requeridos.”

export const GeneralSchema = (userInfo, managers) =>
  Yup.object().shape({
    manager: Yup.lazy((value, context) =>
      managers.length > 0
        ? Yup.string().required("El campo es requerido.")
        : Yup.string().nullable()
    ),
    files: Yup.array()
      .min(1, "Al menos un documento es requerido.")
      .required("Al menos un documento es requerido.")
      .uniqueDocs()
      .test("checkDocumentsByVehicleType", "", function (list, c) {
        const types = calculateMapperTypes(list);
        const vehicleType = c.parent.vehicleType;
        const isSpecialVehicles =
          vehicleType === "CHASIS CABINADO" || vehicleType === "CAMION";
        const hasBill = types?.["Factura"] > 0;
        const hasDS55 = types?.["DS55"] > 0;

        if (isSpecialVehicles && !hasBill && !hasDS55) {
          return this.createError({
            path: "files",
            message: "La factura y el DS55 son requeridos.",
          });
        }

        if (!hasBill) {
          return this.createError({
            path: "files",
            message: "La factura es requerida.",
          });
        }

        if (!isSpecialVehicles) return true;

        if (!hasDS55) {
          return this.createError({
            path: "files",
            message: "El DS55 es requerido.",
          });
        }

        return true;
      })
      .of(
        Yup.object().shape({
          file: validateFile("El documento es invalido.", ["application/pdf"]),
          type: Yup.string()
            .required("Seleccione el tipo documental correspondiente.")
            .test(
              "uniqueDoc",
              "No se debe repetir el tipo documental.",
              (value, c) => {
                if (!value || value === "Nota de Crédito") return true;
                const types = calculateMapperTypes(c.from?.[1]?.value?.files);
                return !(types?.[value] > 1);
              }
            ),
          id: Yup.string().required(),
        })
      ),
    rut: Yup.lazy((value, context) => {
      return addDistinctValidation(
        validateNativeRut,
        context,
        "rut",
        "Los RUT deben ser diferentes."
      );
    }),
    email: Yup.lazy((value, context) => {
      return !userInfo
        ? addDistinctValidation(
            emailConfirm(
              value,
              context.parent.emailConfirm,
              "El correo electrónico es requerido."
            ),
            context,
            "email",
            "Los correos deben ser diferentes."
          )
        : addDistinctValidation(
            validateEmail("El correo electrónico es requerido."),
            context,
            "email",
            "Los correos deben ser diferentes."
          );
    }),
    emailConfirm: Yup.lazy((value, context) => {
      return !userInfo
        ? emailConfirm(
            value,
            context.parent.email,
            "La confirmación de correo electrónico es requerida."
          )
        : Yup.string();
    }),
    name: validateName("nombre"),
    firstName: validateName("apellido paterno"),
    lastName: Yup.string()
      .matches(/^[A-Za-zÀ-ÿ ]*$/, "El apellido materno no es válido.")
      .max(100, "La cantidad máxima de caracteres es 100."),
    phone: Yup.lazy((value, context) => {
      return addDistinctValidation(
        Yup.string()
          .required("El teléfono es requerido.")
          .phone(context.parent.countryCode),
        context,
        "phone",
        "Los teléfonos deben ser diferentes."
      );
    }),
    recaptcha: Yup.lazy((value, context) =>
      !userInfo && AppSettings.production
        ? Yup.string().required("El recaptcha es requerido.")
        : Yup.string()
    ),
    anual: validateYear,
    netAmount: validateNumber,
    vehicleType: Yup.string().required("El tipo de vehículo es requerido."),
    fuel_type: Yup.string().required("El tipo de combustible es requerido."),
    billIssuerSelected: Yup.mixed(),
    billIssuer: Yup.string().required("El emisor de factura es requerido."),
    dateOfIssue: validateDateHandler(
      "La fecha de emisión de factura es requerida."
    ),

    isOwner: Yup.string().nullable().required("Debe seleccionar una opción."),
    personType: Yup.lazy((value, context) =>
      context.parent.isOwner === "0"
        ? Yup.string().nullable().required("El tipo de persona es requerido.")
        : Yup.string().nullable()
    ),

    personInfo: Yup.lazy((value, context) =>
      context.parent.isOwner === "0" && context.parent.personType === "NATURAL"
        ? Yup.object().shape({
            rut: Yup.lazy((value, context) => {
              return validateNativeRut.test(
                "distinct-element",
                "Los RUT deben ser diferentes.",
                (value) => value !== context.from?.[1]?.value?.rut
              );
            }),
            email: Yup.lazy((value, context) => {
              return distinctEmail(
                value,
                context.from?.[1]?.value?.email,
                "El correo electrónico es requerido."
              );
            }),
            name: validateName("nombre"),
            firstName: validateName("apellido paterno"),
            lastName: Yup.string()
              .matches(/^[A-Za-zÀ-ÿ ]*$/, "El apellido materno no es válido.")
              .max(100, "La cantidad máxima de caracteres es 100."),
            phone: Yup.lazy((value, context) => {
              return Yup.string()
                .required("El teléfono es requerido.")
                .phone(context.parent.countryCode, "personInfo.phone")
                .test(
                  "distinct-element",
                  "Los teléfonos deben ser diferentes.",
                  (value) => value !== context.from?.[1]?.value?.phone
                );
            }),
          })
        : Yup.object().shape({})
    ),

    ownerBillInfo: Yup.lazy((value, context) =>
      context.parent.isOwner === "0" && context.parent.personType === "JURÍDICA"
        ? Yup.object().shape({
            rut: validateLegalRut,
            businessType: validateIfHaveAtLeastLetterRequired("razón social"),
            economicActivity: validateIfHaveAtLeastLetter(
              "actividad económica"
            ),
            email: Yup.lazy((value, context) => {
              return distinctEmail(
                value,
                context.from?.[1]?.value?.email,
                "El correo electrónico es requerido."
              );
            }),
            phone: Yup.lazy((value, context) => {
              return Yup.string()
                .required("El teléfono es requerido.")
                .phone(context.parent.countryCode, "ownerBillInfo.phone")
                .test(
                  "distinct-element",
                  "Los teléfono deben ser diferentes.",
                  (value) => value !== context.from?.[1]?.value?.phone
                );
            }),
          })
        : Yup.object().shape({})
    ),
  });
